import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";

import { PageHeader } from "../../../components/portfolio-risk-concentration/PageHeader";
import { PowerBIEmbed } from "../../../components/portfolio-risk-concentration/PowerBIEmbed";
import {
  PrcFilterBar,
  SCOPE_LABEL,
  useInvestorFilterOptions,
  usePrcFilters,
  type FacilityFilter,
  type ProductFilter,
} from "../../../components/portfolio-risk-concentration/PrcFilterBar";
import { useBookLabels } from "../../../utils/portfolio-risk-concentration/api/facilities";
import { StepShell } from "../../../components/portfolio-risk-concentration/StepShell";
import { SupersetEmbed } from "../../../components/portfolio-risk-concentration/SupersetEmbed";
import { Button } from "../../../ui/button";
import { Calendar } from "../../../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../../ui/popover";
import {
  powerBIUrlForScope,
  usePowerBIReport,
} from "../../../utils/portfolio-risk-concentration/api/powerbi";
import { useSupersetDashboard } from "../../../utils/portfolio-risk-concentration/api/superset-views";

/**
 * Snapshot — the formatted, point-in-time view of what Monitor shows.
 *
 * ── The filters live on this page, and now actually work ─────────────────────────────────
 * They used to be inside the embedded dashboard's own filter bar, with a decorative copy up
 * here: the page passed `urlParams={{scope}}` to the embedded SDK, which Superset does not bind
 * to a native filter, and this deployment has no template processing either. So the selector
 * changed the panel heading while the numbers came from whatever the viewer had left in the
 * in-frame filter bar. Now the selection travels as guest-token RLS (see SupersetEmbed), the
 * in-frame bar is hidden, and the native filters have been removed from the dashboard.
 *
 * ── One panel, one dashboard, always ─────────────────────────────────────────────────────
 * This page used to show the `monitor` dashboard beside `monitor-proposed` (sql/50_ and
 * sql/60_) so a proposal could be judged against the book it would apply to. Both are gone
 * from here: Snapshot shows the approved book only. Monitor's own **Basis** control
 * (Approved / Proposed) is where a pending proposal is judged now, and it re-scores through
 * the same `prc_calc` functions rather than a parallel set of charts.
 *
 * `monitor` went the same way for the same reason — it was the panel the Internal scope
 * swapped to, and swapping the artefact is not filtering it. See DASHBOARD_KEY.
 *
 * Both keys are deliberately left registered in SUPERSET_DASHBOARDS and still exist in
 * Superset. Nothing on this page renders them, which costs nothing; un-registering them
 * would break their embed records for whoever wants them back — and prc-api still builds
 * correct RLS for both, so a caller that asks for one gets a filtered dashboard.
 *
 * The Power BI panel below is a placeholder with all its wiring in place. It renders an
 * explicit "not yet connected" state until POWERBI_REPORT_URL is set on prc-api.
 */
/**
 * The Portfolio Concentration Summary (sql/76_), in its three column layouts.
 *
 * All three sit on the SAME view, the same row grain and the same chart definition; they
 * differ only in which columns the chart lists. That exists because guest-token RLS narrows
 * ROWS and cannot narrow COLUMNS — Superset has no per-viewer column visibility, and the
 * embedded SDK cannot rewrite a saved chart. So picking CAP 2 on the combined dashboard did
 * filter (32 limits down to 26) while leaving the five CAP 1 columns on screen reading "N/A",
 * which reads exactly like a filter that did nothing.
 *
 * Row filtering is still applied on top of these: the CAP 1 dashboard is sent
 * `cap1_limit_pct IS NOT NULL` as well, or CAP-2-only limits would show up as blank rows.
 *
 * ── Scope does NOT swap the dashboard ───────────────────────────────────────────────────
 * Internal used to point at `monitor`, the long-format view, because the summary's
 * side-by-side layout has no internal counterpart — an internal limit is not scoped to a
 * book at all (see sql/71_'s ranking note). The cost was that one click replaced the whole
 * panel with a differently-shaped table: different columns, different row grain, a different
 * reading of every number on screen. Swapping between these three is a column choice over one
 * table; swapping to `monitor` was a different table.
 *
 * So the Scope toggle is INERT here. It stays on the bar because it is the product's one
 * filter vocabulary and every page shows the same set — hiding it on this page alone would be
 * the reflow problem PrcFilterBar already calls out. `scope` is simply not sent to the guest
 * token: the summary view selects `'External'::text AS scope`, so a clause over it would
 * match everything or nothing, and prc-api drops it with a warning even if it is sent
 * (_SCOPE_FIXED_EXTERNAL_KEYS). Internal limits are read on Monitor, the long-format surface
 * built for them.
 */
// The Superset summary dashboards that exist, per book. A facility with no dashboard of its own reads the whole summary ("ALL"): see the
// lookup below. The dashboards are Superset assets with fixed columns, not something this page can list from the database.
const DASHBOARD_KEY_FOR_BOOK: Record<FacilityFilter, string> = {
  ALL: "summary",
  CAP1: "summary-cap1",
  CAP2: "summary-cap2",
};

/** Loans are CAP 1 and leases are CAP 2 — the mapping prc-api applies in _PRODUCT_TO_BOOK. */
/**
 * The two contract-level dashboards under the summary. Same filters as the summary (facility,
 * product, investor, asOf); prc-api turns them into Superset filters, so nothing is added on the
 * Superset side. Unlike the summary, neither swaps by book: they have one layout.
 */
const CONTRACT_PANELS = [
  { key: "contract-detail", title: "Contract Detail" },
  { key: "contract-ineligible", title: "Ineligible Contracts" },
] as const;

const BOOK_FOR_PRODUCT: Record<ProductFilter, FacilityFilter> = {
  ALL: "ALL",
  LOAN: "CAP1",
  LEASE: "CAP2",
};

/**
 * The book the two selectors add up to, or "ALL" when they do not agree on one.
 *
 * Facility and Loans/Leases are separate controls over the same dimension and prc-api
 * INTERSECTS them (_book_clauses), so this has to read them the same way the clauses do:
 *
 *   nothing picked      -> ALL, both column groups
 *   one of them picked  -> that book
 *   both, same book     -> that book
 *   both, DIFFERENT     -> ALL. "CAP 1 + Leases" is a contradiction; prc-api answers it with
 *                          zero rows, and the combined layout is the right frame for an empty
 *                          answer — a one-book layout would imply the other book was the
 *                          thing excluded.
 */
function resolveBook(facility: FacilityFilter, product: ProductFilter): FacilityFilter {
  const books = new Set(
    [facility, BOOK_FOR_PRODUCT[product]].filter((b) => b !== "ALL"),
  );
  return books.size === 1 ? ([...books][0] as FacilityFilter) : "ALL";
}

export default function SnapshotPage() {
  const bookLabels = useBookLabels();
  const { investorsWithAll } = useInvestorFilterOptions();
  const [searchParams, setSearchParams] = useSearchParams();
  // The shared filter state, identical to every other page.
  const prcFilters = usePrcFilters(searchParams, setSearchParams);
  const { scope, facility, product, investor } = prcFilters;

  // As-of date, matching Monitor and Capacity. Local state like theirs — it is a viewing
  // position rather than part of the shared filter set, and none of the three pages puts it
  // in the URL.
  const [asOf, setAsOf] = useState<Date>(() => new Date());
  const asOfDate = format(asOf, "yyyy-MM-dd");

  // Which column layout the two book selectors add up to. The FILTERS below are unchanged by
  // this — the book still travels as RLS, because these dashboards drop the other book's
  // columns but not its rows.
  const book = resolveBook(facility, product);
  // a facility added in Manage Capital has no dashboard of its own yet: the summary
  const dashboardKey = DASHBOARD_KEY_FOR_BOOK[book] ?? DASHBOARD_KEY_FOR_BOOK.ALL!;
  const approved = useSupersetDashboard(dashboardKey);
  const contractDetail = useSupersetDashboard(CONTRACT_PANELS[0].key);
  const contractIneligible = useSupersetDashboard(CONTRACT_PANELS[1].key);
  const contractDashboards = [contractDetail, contractIneligible];
  const { data: powerbi, isLoading: powerbiLoading } = usePowerBIReport("monitor");

  // Rebuilt each render, but SupersetEmbed keys its effect on the JSON so an unchanged
  // selection does not remount the iframe.
  //
  // Every value here becomes a guest-token RLS clause in prc-api. `asOf` resolves server-side
  // to the newest snapshot at or before the chosen day — the same rule the REST endpoints
  // use — because the warehouse holds four snapshots at irregular intervals and an exact
  // date match would empty the dashboard on almost every day of the year.
  //
  // No `scope`: see DASHBOARD_KEY_FOR_BOOK. It is the one control on this bar that does not
  // narrow this dashboard, and sending it would only earn a warning back.
  const filters = { facility, product, investor, asOf: asOfDate };

  const bookSuffix = book === "ALL" ? "" : ` · ${bookLabels[book] ?? book}`;

  const panelTitle = `Portfolio Concentration Summary — ${format(asOf, "d MMM yyyy")}${bookSuffix}`;

  /**
   * Where "Expand" opens: this app's own full-tab route, NOT Superset.
   *
   * Everything the expanded tab needs travels in the URL, because a new tab shares no state
   * with its opener — the dashboard key so it mounts the same column layout, and the same
   * filter values so prc-api rebuilds the same guest-token RLS. Miss one and the expanded
   * view would quietly answer a wider question than the panel it came from.
   *
   * `scope` is not sent, exactly as it is not sent to the panel: it is inert on these
   * dashboards (see DASHBOARD_KEY_FOR_BOOK) and prc-api would only warn about it.
   */
  const expandUrl = (key: string, title: string) =>
    `/risk-concentrations/expanded?${new URLSearchParams({
      key,
      title,
      facility,
      product,
      investor,
      asOf: asOfDate,
    }).toString()}`;
  const expandHref = expandUrl(dashboardKey, panelTitle);

  return (
    <StepShell step={3}>
      <PageHeader
        title="Snapshot"
      />

      <div className="mt-6">
        <PrcFilterBar
          scope={scope}
          onScopeChange={prcFilters.setScope}
          facility={facility}
          onFacilityChange={prcFilters.setFacility}
          product={product}
          onProductChange={prcFilters.setProduct}
          investor={investor}
          onInvestorChange={prcFilters.setInvestor}
          investorOptions={investorsWithAll}
          right={
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-9 gap-2">
                  <CalendarIcon className="h-3.5 w-3.5" />
                  As of <span suppressHydrationWarning>{format(asOf, "d MMM yyyy")}</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={asOf}
                  onSelect={(d) => d && setAsOf(d)}
                  defaultMonth={asOf}
                  captionLayout="dropdown-buttons"
                  fromYear={2015}
                  toYear={new Date().getFullYear()}
                  disabled={{ after: new Date() }}
                />
              </PopoverContent>
            </Popover>
          }
        />

      </div>


      {/* Full width now that it is one panel: the table has enough columns that the two-column
          grid this used to sit in truncated them. */}
      <div className="mt-4">
        <SupersetEmbed
          title={panelTitle}
          uuid={approved.data?.uuid}
          supersetDomain={approved.data?.supersetDomain}
          loading={approved.isLoading}
          dashboardKey={dashboardKey}
          filters={filters}
          expandHref={expandHref}
        />
      </div>


      {/* The contract tables behind the summary: who is inside risk concentration, and who is
          left out. Titled with the date and book like the summary above. */}
      {CONTRACT_PANELS.map((panel, i) => {
        const title = `${panel.title} — ${format(asOf, "d MMM yyyy")}${bookSuffix}`;
        const dashboard = contractDashboards[i]!;
        return (
          <div key={panel.key} className="mt-6">
            <SupersetEmbed
              title={title}
              uuid={dashboard.data?.uuid}
              supersetDomain={dashboard.data?.supersetDomain}
              loading={dashboard.isLoading}
              dashboardKey={panel.key}
              filters={filters}
              expandHref={expandUrl(panel.key, title)}
            />
          </div>
        );
      })}

      <div className="mt-6">
        <PowerBIEmbed
          productName={
            powerbi?.title
              ? `${powerbi.title}`
              : `Portfolio Risk Concentration (Power BI)`
          }
          src={powerBIUrlForScope(powerbi, SCOPE_LABEL[scope])}
          loading={powerbiLoading}
          emptyMessage="Power BI is not connected yet. The panel and its endpoint are in place — setting POWERBI_REPORT_URL on prc-api is all that is left."
        />
      </div>
    </StepShell>
  );
}
